// ─── src/components/dashboard/EcosystemPerceptionTable.tsx ───────────────────

import { useState } from "react";
import { Eye, Trash2, X } from "lucide-react";
import type { EcosystemResponse, Indicator } from "../../types/ecosystemPerception";
import { INDICATOR_LABELS } from "../../types/ecosystemPerception";

interface Props {
  rows: EcosystemResponse[];
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function npsColor(n: number) {
  if (n >= 9) return "text-emerald-400";
  if (n >= 7) return "text-amber-400";
  return "text-red-400";
}

const INDICATOR_ORDER: Indicator[] = [
  "trust", "awareness", "reach", "value", "belonging", "communication", "alignment",
];

export function EcosystemPerceptionTable({ rows, onDelete, isDeleting }: Props) {
  const [selected, setSelected] = useState<EcosystemResponse | null>(null);

  if (rows.length === 0) {
    return <div className="card p-10 text-center text-brand-muted">No responses yet.</div>;
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Name</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Governorate</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Role</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Age</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">NPS</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Date</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                  <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>
                  <td className="px-4 py-3 text-white font-medium whitespace-nowrap" dir="rtl">{r.fullNameAr}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">{r.governorate}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap max-w-[160px] truncate" dir="rtl">{r.roleWithStartupSyria}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">{r.ageGroup}</td>
                  <td className={`px-4 py-3 font-bold ${npsColor(r.nps)}`}>{r.nps}</td>
                  <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{formatDate(r.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelected(r)} title="View details"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => onDelete(r.id)} disabled={isDeleting} title="Remove"
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailModal row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function DetailModal({ row, onClose }: { row: EcosystemResponse; onClose: () => void }) {
  const answerEntries = Object.entries(row.answers ?? {});
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.fullNameAr}</p>
            <p className="text-brand-muted text-sm" dir="rtl">{row.governorate} · {row.roleWithStartupSyria}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Indicator scores */}
          <div>
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-3">Indicator scores (1–5) · NPS {row.nps}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {INDICATOR_ORDER.map((ind) => (
                <div key={ind} className="rounded-lg border border-brand-border bg-brand-navy px-3 py-2.5">
                  <p className="text-brand-muted text-[11px] truncate">{INDICATOR_LABELS[ind]}</p>
                  <p className="text-white text-lg font-bold">{row.scores?.[ind]?.toFixed(2) ?? "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Demographics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" dir="rtl">
            <Meta label="المدينة/المنطقة" value={row.cityArea} />
            <Meta label="الفئة العمرية" value={row.ageGroup} />
            <Meta label="الجنس" value={row.gender} />
            <Meta label="الوضع المهني" value={row.professionalStatus} />
          </div>

          {/* All answers */}
          <div>
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-3">Answers</p>
            <div className="space-y-2.5" dir="rtl">
              {answerEntries.map(([k, v]) => (
                <div key={k} className="rounded-lg border border-brand-border/60 px-3 py-2">
                  <p className="text-brand-muted text-[11px] uppercase" dir="ltr">{k}</p>
                  <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">
                    {Array.isArray(v) ? v.join(" • ") : v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-[11px]">{label}</p>
      <p className="text-white">{value}</p>
    </div>
  );
}
